// D10 — Shared presentation shell for admin booking-detail surfaces.
//
// Centralizes the Dialog container + header + body-spacing + footer-row
// across the three sibling detail components:
//   - SubscriptionDetailDialog   (operator's reference look)
//   - ActivityDetailModal        (canonical open-match)
//   - BookingDetailPanel         (Lager 1 — plain private + legacy open-match)
//
// Goal: visual unity ("alla tre adopterar samma skal") WITHOUT merging them
// into a god-component. The shell knows nothing about subscription/activity/
// private — consumers pass title/subtitle/body/footer and keep all domain
// state + nested companion dialogs in their own files (Radix Dialog supports
// nested instances via context — see SubscriptionDetailDialog's AlertDialog
// + CreateSubscriptionInvoiceDialog sibling pattern as the canonical example).
//
// API stays deliberately small (no DetailSection / DetailField sub-primitives
// extracted in this iteration — would be over-engineering for 3 consumers
// per the D10 "skuld förklädd till städning" guidance).

import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface BookingDetailShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Header title (large, primary). */
  title: ReactNode;
  /** Compact subtitle row — typically date/time/court summary. Optional. */
  subtitle?: ReactNode;
  /** Optional badge rendered inline with the title (e.g. booking-type tag).
   *  Keep it short — multiple/wide badges should go in the body instead. */
  headerBadge?: ReactNode;
  /** Domain-specific body content. Consumers render their own sections,
   *  field grids, lists, edit forms etc. inside this slot. */
  children: ReactNode;
  /** Optional action row rendered with border-top + flex-wrap + justify-end.
   *  Pass `null` to hide (e.g. when an inline edit form has its own buttons). */
  footer?: ReactNode;
  /** Width / height override. Default matches SubscriptionDetailDialog's
   *  current canonical look (max-w-md, max-h-[85vh], overflow-y-auto). */
  contentClassName?: string;
}

const DEFAULT_CONTENT_CN = "max-w-md max-h-[85vh] overflow-y-auto";

/**
 * Shared admin booking-detail dialog shell.
 *
 * Consumers wrap their content like this:
 *
 *   <BookingDetailShell
 *     open={open} onOpenChange={onOpenChange}
 *     title="Aktivitetsdetaljer"
 *     subtitle="Tor 14:00–15:30 · Bana 1"
 *     footer={<><Button>Stäng</Button>…</>}
 *   >
 *     {body}
 *   </BookingDetailShell>
 *
 * Companion dialogs (AlertDialog, secondary Dialog, etc.) render as siblings
 * outside this shell — Radix handles nested Dialog state cleanly.
 */
export function BookingDetailShell({
  open,
  onOpenChange,
  title,
  subtitle,
  headerBadge,
  children,
  footer,
  contentClassName,
}: BookingDetailShellProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={contentClassName ?? DEFAULT_CONTENT_CN}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{title}</DialogTitle>
            {headerBadge}
          </div>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3 pt-2 text-sm">
          {children}
          {footer && (
            <div
              className="flex flex-wrap gap-2 pt-2 border-t justify-end"
              data-testid="booking-detail-shell-footer"
            >
              {footer}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BookingDetailShell;
